/**
 * Buildings, one drawer per style. All share the same light model and the
 * same door rule (doorOf): the door sits on the +y face (rot 0) or the +x face
 * (rot 1), so the camera always sees it.
 */
import { doorOf, footprint, hash01, type Prop } from '../../../shared/world.ts'
import { mix, shade } from './color.ts'
import { box, castShadow, cyl, iso, poly, type Pt } from './iso.ts'

type Ctx = CanvasRenderingContext2D

const faceY = (y: number, x0: number, x1: number, z0: number, z1: number): Pt[] => [iso(x0, y, z0), iso(x1, y, z0), iso(x1, y, z1), iso(x0, y, z1)]
const faceX = (x: number, y0: number, y1: number, z0: number, z1: number): Pt[] => [iso(x, y0, z0), iso(x, y1, z0), iso(x, y1, z1), iso(x, y0, z1)]

/** Default wall height per style, in z units. */
export function wallHeight(b: Prop): number {
  if (b.h) return b.h
  switch (b.style) {
    case 'shack': return 1.8
    case 'shop': return 2.6
    case 'tower': return 8
    case 'lighthouse': return 6
    case 'pagoda': return 3
    default: return 2.3
  }
}

/** Height for sprite bounds and see-through tests. */
export function buildingTop(b: Prop): number {
  const H = wallHeight(b)
  const { w, d } = footprint(b)
  switch (b.style) {
    case 'tower': return H + 1.2
    case 'lighthouse': return H + 2.4
    case 'pagoda': return H * 1.9 + 1.6
    default: return H + Math.min(w, d) * 0.45 + 1
  }
}

/** Window positions along the front face, skipping the door. */
export function windowsOf(b: Prop, step = 1.5): number[] {
  const { w, d } = footprint(b)
  const dr = doorOf(b)
  const len = b.rot ? d : w
  const start = b.rot ? b.y : b.x
  const doorAt = b.rot ? dr.y : dr.x
  const out: number[] = []
  for (let u = start + 0.6; u + 0.8 <= start + len - 0.4; u += step) if (Math.abs(u + 0.4 - (doorAt + 0.5)) > 1.0) out.push(u)
  return out
}

export function drawBuilding(ctx: Ctx, b: Prop, day: number): void {
  switch (b.style) {
    case 'tower': return tower(ctx, b, day)
    case 'pagoda': return pagoda(ctx, b, day)
    case 'lighthouse': return lighthouse(ctx, b, day)
    default: return house(ctx, b, day)
  }
}

// ─── Houses: cottage, shop, shack, canal house, teahouse ──────────────────

function house(ctx: Ctx, b: Prop, day: number): void {
  const { w, d } = footprint(b)
  const x = b.x, y = b.y
  const style = b.style ?? 'cottage'
  const wall = b.color ?? '#f6e7d4'
  const roof = b.roof ?? '#c9705a'
  const night = 1 - day
  const H = wallHeight(b)
  castShadow(ctx, x, y, w, d, H + 1.2, 0.18)
  const plinth = style === 'canal' ? '#5b6170' : style === 'teahouse' ? '#6b5a4a' : shade(wall, -0.25)
  box(ctx, x - 0.08, y - 0.08, 0, w + 0.16, d + 0.16, style === 'canal' ? 0.45 : 0.22, plinth, { edge: false })
  const z0 = style === 'canal' ? 0.45 : 0.22
  box(ctx, x, y, z0, w, d, H - z0, wall, { faces: { top: false } })
  const trim = style === 'teahouse' ? '#5a3d2b' : shade(wall, -0.32)
  if (style === 'teahouse') {
    // Dark timber frame over paper-white walls.
    for (let u = x; u <= x + w + 0.01; u += w / Math.max(2, Math.round(w / 1.6))) poly(ctx, faceY(y + d + 0.002, u - 0.06, u + 0.06, z0, H), trim)
    for (let u = y; u <= y + d + 0.01; u += d / Math.max(2, Math.round(d / 1.6))) poly(ctx, faceX(x + w + 0.002, u - 0.06, u + 0.06, z0, H), shade(trim, -0.1))
    poly(ctx, faceY(y + d + 0.002, x, x + w, H - 0.14, H), trim)
    poly(ctx, faceX(x + w + 0.002, y, y + d, H - 0.14, H), shade(trim, -0.1))
  } else if (style === 'shack') {
    ctx.strokeStyle = 'rgba(0,0,0,0.12)'; ctx.lineWidth = 1
    for (let z = z0 + 0.25; z < H; z += 0.25) {
      const a = iso(x, y + d, z), c = iso(x + w, y + d, z), e = iso(x + w, y, z)
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(c.x, c.y); ctx.lineTo(e.x, e.y); ctx.stroke()
    }
  } else if (style !== 'canal') {
    poly(ctx, faceY(y + d + 0.001, x + w - 0.12, x + w, z0, H), trim)
    poly(ctx, faceX(x + w + 0.001, y + d - 0.12, y + d, z0, H), shade(trim, -0.1))
    poly(ctx, faceY(y + d + 0.001, x, x + 0.12, z0, H), trim)
    poly(ctx, faceX(x + w + 0.001, y, y + 0.12, z0, H), shade(trim, -0.1))
  }
  // Windows.
  const lit = mix(style === 'canal' ? '#2f3442' : '#9fd3ef', '#ffd98a', night)
  const winY = (u: number, frame: string) => {
    if (style === 'teahouse') { shoji(ctx, faceY, y + d + 0.003, u, u + 0.8, 1.0, 1.9, night, 0); return }
    if (style === 'canal') { lattice(ctx, faceY, y + d + 0.003, u, u + 0.8, 1.1, 1.75, lit, '#5a3d2b', 0); return }
    poly(ctx, faceY(y + d + 0.002, u - 0.06, u + 0.86, 0.94, 1.86), frame)
    poly(ctx, faceY(y + d + 0.003, u, u + 0.8, 1.0, 1.8), lit)
    poly(ctx, faceY(y + d + 0.004, u + 0.38, u + 0.42, 1.0, 1.8), frame)
    poly(ctx, faceY(y + d + 0.004, u, u + 0.8, 1.38, 1.42), frame)
  }
  const winX = (u: number, frame: string) => {
    if (style === 'teahouse') { shoji(ctx, faceX, x + w + 0.003, u, u + 0.8, 1.0, 1.9, night, -0.1); return }
    if (style === 'canal') { lattice(ctx, faceX, x + w + 0.003, u, u + 0.8, 1.1, 1.75, shade(lit, -0.08), '#4a3020', -0.1); return }
    poly(ctx, faceX(x + w + 0.002, u - 0.06, u + 0.86, 0.94, 1.86), shade(frame, -0.1))
    poly(ctx, faceX(x + w + 0.003, u, u + 0.8, 1.0, 1.8), shade(lit, -0.08))
    poly(ctx, faceX(x + w + 0.004, u + 0.38, u + 0.42, 1.0, 1.8), shade(frame, -0.1))
    poly(ctx, faceX(x + w + 0.004, u, u + 0.8, 1.38, 1.42), shade(frame, -0.1))
  }
  const front = windowsOf(b)
  if (style === 'shop') {
    // A big shop window across the front, and a striped awning.
    const dr = doorOf(b)
    if (!b.rot) {
      poly(ctx, faceY(y + d + 0.002, x + 0.2, x + w - 0.2, 0.3, 1.7), shade(wall, -0.2))
      poly(ctx, faceY(y + d + 0.003, x + 0.3, x + w - 0.3, 0.4, 1.6), mix('#bfe6f5', '#ffe3a0', night))
      for (let u = x + 0.3; u < x + w - 0.3; u += 1.1) poly(ctx, faceY(y + d + 0.004, u, u + 0.06, 0.4, 1.6), shade(wall, -0.2))
      awning(ctx, x + 0.1, x + w - 0.1, y + d, roof, 'y')
      void dr
    } else {
      poly(ctx, faceX(x + w + 0.002, y + 0.2, y + d - 0.2, 0.3, 1.7), shade(wall, -0.25))
      poly(ctx, faceX(x + w + 0.003, y + 0.3, y + d - 0.3, 0.4, 1.6), mix('#acd6e6', '#ffe3a0', night))
      awning(ctx, y + 0.1, y + d - 0.1, x + w, roof, 'x')
    }
    if (H > 2.8) for (let u = (b.rot ? x : y) + 0.6; u + 0.8 <= (b.rot ? x + w : y + d) - 0.4; u += 1.5) (b.rot ? winY : winX)(u, '#ffffff')
  } else if (b.rot) {
    for (let u = x + 0.6; u + 0.8 <= x + w - 0.4; u += 1.5) winY(u, '#ffffff')
    for (const u of front) winX(u, '#ffffff')
  } else {
    for (const u of front) winY(u, '#ffffff')
    for (let u = y + 0.6; u + 0.8 <= y + d - 0.4; u += 1.5) winX(u, '#ffffff')
  }
  door(ctx, b, day, style === 'shop' ? undefined : shade(roof, 0.08))
  roofFor(ctx, b, style, H)
}

function awning(ctx: Ctx, u0: number, u1: number, at: number, color: string, axis: 'x' | 'y'): void {
  const z = 1.85
  const n = Math.max(3, Math.round((u1 - u0) / 0.35))
  for (let i = 0; i < n; i++) {
    const a = u0 + (i / n) * (u1 - u0), c = u0 + ((i + 1) / n) * (u1 - u0)
    const pts = axis === 'y'
      ? [iso(a, at, z + 0.25), iso(c, at, z + 0.25), iso(c, at + 0.55, z - 0.05), iso(a, at + 0.55, z - 0.05)]
      : [iso(at, a, z + 0.25), iso(at, c, z + 0.25), iso(at + 0.55, c, z - 0.05), iso(at + 0.55, a, z - 0.05)]
    poly(ctx, pts, i % 2 ? '#ffffff' : axis === 'x' ? shade(color, -0.08) : color)
  }
}

function shoji(ctx: Ctx, face: typeof faceY, at: number, u0: number, u1: number, z0: number, z1: number, night: number, lit: number): void {
  poly(ctx, face(at, u0 - 0.05, u1 + 0.05, z0 - 0.05, z1 + 0.05), shade('#5a3d2b', lit))
  poly(ctx, face(at + 0.001, u0, u1, z0, z1), shade(mix('#f6f1e4', '#ffe2a8', night), lit))
  for (let k = 1; k < 3; k++) poly(ctx, face(at + 0.002, u0 + (u1 - u0) * k / 3 - 0.02, u0 + (u1 - u0) * k / 3 + 0.02, z0, z1), shade('#8a6a4a', lit))
  for (let k = 1; k < 4; k++) poly(ctx, face(at + 0.002, u0, u1, z0 + (z1 - z0) * k / 4 - 0.015, z0 + (z1 - z0) * k / 4 + 0.015), shade('#8a6a4a', lit))
}

function lattice(ctx: Ctx, face: typeof faceY, at: number, u0: number, u1: number, z0: number, z1: number, glass: string, wood: string, lit: number): void {
  poly(ctx, face(at, u0 - 0.06, u1 + 0.06, z0 - 0.06, z1 + 0.06), shade(wood, lit))
  poly(ctx, face(at + 0.001, u0, u1, z0, z1), glass)
  for (let k = 1; k < 4; k++) poly(ctx, face(at + 0.002, u0 + (u1 - u0) * k / 4 - 0.015, u0 + (u1 - u0) * k / 4 + 0.015, z0, z1), shade(wood, lit))
  for (let k = 1; k < 4; k++) poly(ctx, face(at + 0.002, u0, u1, z0 + (z1 - z0) * k / 4 - 0.015, z0 + (z1 - z0) * k / 4 + 0.015), shade(wood, lit))
}

function door(ctx: Ctx, b: Prop, day: number, awn?: string): void {
  const { w, d } = footprint(b)
  const x = b.x, y = b.y
  const night = 1 - day
  const dr = doorOf(b)
  const style = b.style ?? 'cottage'
  const wood = style === 'canal' ? '#3a2a22' : style === 'teahouse' ? '#5a3d2b' : '#8a5a3a'
  const top = style === 'shack' ? 1.35 : 1.78
  const glass = mix('#b9e3f5', '#ffd98a', night)
  if (!b.rot) {
    const u = dr.x
    poly(ctx, faceY(y + d + 0.002, u + 0.1, u + 0.9, 0.22, top), style === 'canal' ? '#2a2420' : '#ffffff')
    poly(ctx, faceY(y + d + 0.003, u + 0.16, u + 0.84, 0.22, top - 0.06), wood)
    if (style === 'teahouse') poly(ctx, faceY(y + d + 0.004, u + 0.16, u + 0.84, top - 0.6, top - 0.06), '#2f4b7a') // noren curtain
    else if (style !== 'canal') poly(ctx, faceY(y + d + 0.004, u + 0.24, u + 0.76, 1.1, top - 0.18), glass)
    if (style === 'canal') redLanterns(ctx, [iso(u - 0.05, y + d + 0.1, 1.9), iso(u + 1.05, y + d + 0.1, 1.9)])
    if (awn && style !== 'canal' && style !== 'teahouse') {
      poly(ctx, [iso(u - 0.05, y + d, 1.98), iso(u + 1.05, y + d, 1.98), iso(u + 1.05, y + d + 0.45, 1.75), iso(u - 0.05, y + d + 0.45, 1.75)], awn)
      for (let i = 0; i < 4; i++) poly(ctx, [iso(u - 0.05 + i * 0.275, y + d, 1.98), iso(u + 0.09 + i * 0.275, y + d, 1.98), iso(u + 0.09 + i * 0.275, y + d + 0.45, 1.75), iso(u - 0.05 + i * 0.275, y + d + 0.45, 1.75)], 'rgba(255,255,255,0.55)')
    }
    if (b.text) faceSign(ctx, b.text, iso(u + 0.5, y + d + 0.01, Math.min(style === 'shop' ? 2.25 : 2.2, wallHeight(b) - 0.12)), 1, b.color ?? '#f6e7d4', style)
  } else {
    const u = dr.y
    poly(ctx, faceX(x + w + 0.002, u + 0.1, u + 0.9, 0.22, top), style === 'canal' ? '#2a2420' : shade('#ffffff', -0.1))
    poly(ctx, faceX(x + w + 0.003, u + 0.16, u + 0.84, 0.22, top - 0.06), shade(wood, -0.1))
    if (style === 'teahouse') poly(ctx, faceX(x + w + 0.004, u + 0.16, u + 0.84, top - 0.6, top - 0.06), '#273f68')
    else if (style !== 'canal') poly(ctx, faceX(x + w + 0.004, u + 0.24, u + 0.76, 1.1, top - 0.18), shade(glass, -0.08))
    if (style === 'canal') redLanterns(ctx, [iso(x + w + 0.1, u - 0.05, 1.9), iso(x + w + 0.1, u + 1.05, 1.9)])
    if (awn && style !== 'canal' && style !== 'teahouse') {
      poly(ctx, [iso(x + w, u - 0.05, 1.98), iso(x + w, u + 1.05, 1.98), iso(x + w + 0.45, u + 1.05, 1.75), iso(x + w + 0.45, u - 0.05, 1.75)], shade(awn, -0.1))
      for (let i = 0; i < 4; i++) poly(ctx, [iso(x + w, u - 0.05 + i * 0.275, 1.98), iso(x + w, u + 0.09 + i * 0.275, 1.98), iso(x + w + 0.45, u + 0.09 + i * 0.275, 1.75), iso(x + w + 0.45, u - 0.05 + i * 0.275, 1.75)], 'rgba(255,255,255,0.5)')
    }
    if (b.text) faceSign(ctx, b.text, iso(x + w + 0.01, u + 0.5, Math.min(2.2, wallHeight(b) - 0.12)), -1, b.color ?? '#f6e7d4', style)
  }
}

function redLanterns(ctx: Ctx, at: Pt[]): void {
  for (const p of at) {
    ctx.strokeStyle = '#3a2a22'; ctx.lineWidth = 1
    ctx.beginPath(); ctx.moveTo(p.x, p.y - 8); ctx.lineTo(p.x, p.y - 4); ctx.stroke()
    ctx.fillStyle = '#e0443a'; ctx.beginPath(); ctx.ellipse(p.x, p.y + 1, 4.5, 5.5, 0, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = '#ffd166'; ctx.fillRect(p.x - 2, p.y - 5, 4, 1.5); ctx.fillRect(p.x - 2, p.y + 6, 4, 1.5)
    ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.beginPath(); ctx.ellipse(p.x - 1.5, p.y - 1, 1.2, 2.5, 0, 0, Math.PI * 2); ctx.fill()
  }
}

/** A sign flat on a wall face. `slope` +1 for a +y face, -1 for a +x face. */
export function faceSign(ctx: Ctx, text: string, c: Pt, slope: 1 | -1, wall: string, style = 'cottage'): void {
  ctx.save()
  ctx.translate(c.x, c.y)
  ctx.transform(1, slope * 0.5, 0, 1, 0, 0)
  const canal = style === 'canal'
  ctx.font = `800 ${style === 'shop' ? 10 : 9}px Nunito, ui-rounded, system-ui, sans-serif`
  const tw = ctx.measureText(text).width + 12
  ctx.fillStyle = 'rgba(40,28,64,0.18)'
  ctx.beginPath(); ctx.roundRect(-tw / 2 + 1, -7, tw, 15, 4); ctx.fill()
  ctx.fillStyle = canal ? '#2a2420' : style === 'teahouse' ? '#3a2a22' : '#fffaf2'
  ctx.beginPath(); ctx.roundRect(-tw / 2, -8, tw, 15, canal ? 1 : 4); ctx.fill()
  ctx.strokeStyle = canal ? '#c9a24a' : shade(wall, -0.35); ctx.lineWidth = 1.2; ctx.stroke()
  ctx.fillStyle = canal ? '#ffd166' : style === 'teahouse' ? '#f6efe0' : '#4a3a2c'
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(text, 0, 0)
  ctx.restore()
}

// ─── Roofs ────────────────────────────────────────────────────────────────

function roofFor(ctx: Ctx, b: Prop, style: string, H: number): void {
  const { w, d } = footprint(b)
  const x = b.x, y = b.y
  const roof = b.roof ?? '#c9705a'
  const wall = b.color ?? '#f6e7d4'
  if (style === 'shop') {
    // Flat roof with a parapet and a vent.
    box(ctx, x - 0.05, y - 0.05, H, w + 0.1, d + 0.1, 0.25, shade(wall, -0.12))
    poly(ctx, [iso(x + 0.12, y + 0.12, H + 0.25), iso(x + w - 0.12, y + 0.12, H + 0.25), iso(x + w - 0.12, y + d - 0.12, H + 0.25), iso(x + 0.12, y + d - 0.12, H + 0.25)], shade(roof, -0.25))
    box(ctx, x + w * 0.6, y + d * 0.3, H + 0.25, 0.6, 0.6, 0.35, '#c9ccd6')
    return
  }
  const o = style === 'teahouse' ? 0.55 : style === 'canal' ? 0.35 : 0.28
  const R = Math.min(w, d) * (style === 'shack' ? 0.3 : style === 'teahouse' ? 0.35 : 0.42) + 0.3
  const edgeC = shade(roof, -0.3)
  const along = w >= d
  const tile = style === 'canal' || style === 'teahouse'
  const ridge = shade(roof, tile ? -0.35 : 0.25)
  if (along) {
    const ym = y + d / 2
    poly(ctx, [iso(x - o, y - o, H), iso(x + w + o, y - o, H), iso(x + w + o, ym, H + R), iso(x - o, ym, H + R)], shade(roof, -0.2))
    if (style === 'canal') horseHead(ctx, x + w, y, d, H, R, wall, 'y')
    else poly(ctx, [iso(x + w, y, H), iso(x + w, y + d, H), iso(x + w, ym, H + R)], shade(wall, -0.18))
    const front = [iso(x - o, y + d + o, H - 0.05), iso(x + w + o, y + d + o, H - 0.05), iso(x + w + o, ym, H + R), iso(x - o, ym, H + R)]
    poly(ctx, front, roof)
    ctx.save(); poly(ctx, front); ctx.clip()
    ctx.strokeStyle = tile ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.09)'; ctx.lineWidth = tile ? 1.4 : 1.2
    if (tile) for (let u = x - o; u < x + w + o; u += 0.22) { const a = iso(u, ym, H + R), c = iso(u, y + d + o, H - 0.05); ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(c.x, c.y); ctx.stroke() }
    else for (let k = 1; k < 5; k++) { const f = k / 5; const a = iso(x - o, ym + (d / 2 + o) * f, H + R - (R + 0.05) * f), c = iso(x + w + o, ym + (d / 2 + o) * f, H + R - (R + 0.05) * f); ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(c.x, c.y); ctx.stroke() }
    ctx.restore()
    poly(ctx, [iso(x - o, y + d + o, H - 0.05), iso(x + w + o, y + d + o, H - 0.05), iso(x + w + o, y + d + o, H - 0.17), iso(x - o, y + d + o, H - 0.17)], edgeC)
    if (style !== 'canal') poly(ctx, [iso(x + w + o, y + d + o, H - 0.05), iso(x + w + o, ym, H + R), iso(x + w + o, ym, H + R - 0.12), iso(x + w + o, y + d + o, H - 0.17)], edgeC)
    const r1 = iso(x - o, ym, H + R), r2 = iso(x + w + o, ym, H + R)
    ctx.strokeStyle = ridge; ctx.lineWidth = tile ? 4 : 2.5; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(r1.x, r1.y); ctx.lineTo(r2.x, r2.y); ctx.stroke()
    if (tile) upturn(ctx, [iso(x - o, y + d + o, H - 0.1), iso(x + w + o, y + d + o, H - 0.1), r1, r2], roof)
    if (style === 'cottage' && hash01(x, y, 9) < 0.7) box(ctx, x + w * 0.25, ym - 0.6, H + R * 0.4, 0.4, 0.4, R * 0.8, shade(wall, -0.2))
  } else {
    const xm = x + w / 2
    poly(ctx, [iso(x - o, y - o, H), iso(xm, y - o, H + R), iso(xm, y + d + o, H + R), iso(x - o, y + d + o, H)], shade(roof, -0.2))
    if (style === 'canal') horseHead(ctx, x, y + d, w, H, R, wall, 'x')
    else poly(ctx, [iso(x, y + d, H), iso(x + w, y + d, H), iso(xm, y + d, H + R)], shade(wall, -0.06))
    const front = [iso(x + w + o, y - o, H - 0.05), iso(x + w + o, y + d + o, H - 0.05), iso(xm, y + d + o, H + R), iso(xm, y - o, H + R)]
    poly(ctx, front, shade(roof, -0.08))
    ctx.save(); poly(ctx, front); ctx.clip()
    ctx.strokeStyle = tile ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.09)'; ctx.lineWidth = 1.2
    if (tile) for (let u = y - o; u < y + d + o; u += 0.22) { const a = iso(xm, u, H + R), c = iso(x + w + o, u, H - 0.05); ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(c.x, c.y); ctx.stroke() }
    else for (let k = 1; k < 5; k++) { const f = k / 5; const a = iso(xm + (w / 2 + o) * f, y - o, H + R - (R + 0.05) * f), c = iso(xm + (w / 2 + o) * f, y + d + o, H + R - (R + 0.05) * f); ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(c.x, c.y); ctx.stroke() }
    ctx.restore()
    if (style !== 'canal') poly(ctx, [iso(x + w + o, y + d + o, H - 0.05), iso(xm, y + d + o, H + R), iso(xm, y + d + o, H + R - 0.12), iso(x + w + o, y + d + o, H - 0.17)], edgeC)
    poly(ctx, [iso(x + w + o, y - o, H - 0.05), iso(x + w + o, y + d + o, H - 0.05), iso(x + w + o, y + d + o, H - 0.17), iso(x + w + o, y - o, H - 0.17)], shade(edgeC, -0.1))
    const r1 = iso(xm, y - o, H + R), r2 = iso(xm, y + d + o, H + R)
    ctx.strokeStyle = ridge; ctx.lineWidth = tile ? 4 : 2.5; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(r1.x, r1.y); ctx.lineTo(r2.x, r2.y); ctx.stroke()
    if (tile) upturn(ctx, [iso(x + w + o, y - o, H - 0.1), iso(x + w + o, y + d + o, H - 0.1), r1, r2], roof)
  }
}

/** The curled-up tips of East Asian eaves and ridges. */
function upturn(ctx: Ctx, tips: Pt[], roof: string): void {
  ctx.strokeStyle = shade(roof, -0.4); ctx.lineWidth = 3; ctx.lineCap = 'round'
  for (const [i, p] of tips.entries()) {
    const dir = i % 2 ? 1 : -1
    ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.quadraticCurveTo(p.x + dir * 5, p.y - 1, p.x + dir * 7, p.y - 7); ctx.stroke()
  }
}

/**
 * Ma tou qiang: the stepped white gable walls of Jiangnan houses, each step
 * capped in dark tile, rising above the roof at the visible gable end.
 */
function horseHead(ctx: Ctx, at: number, start: number, len: number, H: number, R: number, wall: string, axis: 'x' | 'y'): void {
  const steps = 3
  const pt = (u: number, z: number) => (axis === 'y' ? iso(at, start + u, z) : iso(start + u, at, z))
  const lit = axis === 'y' ? -0.08 : 0
  // The stepped wall outline: low at both edges, highest in the middle.
  const outline: Pt[] = [pt(0, H)]
  for (let s = 0; s < steps; s++) {
    const u0 = (s / (steps * 2)) * len, u1 = ((s + 1) / (steps * 2)) * len
    const z = H + (R + 0.5) * ((s + 1) / steps)
    outline.push(pt(u0, z), pt(u1, z))
  }
  for (let s = steps - 1; s >= 0; s--) {
    const u0 = len - ((s + 1) / (steps * 2)) * len, u1 = len - (s / (steps * 2)) * len
    const z = H + (R + 0.5) * ((s + 1) / steps)
    outline.push(pt(u0, z), pt(u1, z))
  }
  outline.push(pt(len, H))
  poly(ctx, outline, shade(wall, lit))
  // Dark caps on each step.
  ctx.strokeStyle = '#2f3540'; ctx.lineWidth = 3.5; ctx.lineCap = 'round'
  for (let i = 1; i < outline.length - 2; i += 2) {
    const a = outline[i]!, b2 = outline[i + 1]!
    ctx.beginPath(); ctx.moveTo(a.x, a.y - 1); ctx.lineTo(b2.x, b2.y - 1); ctx.stroke()
  }
}

// ─── Tower, pagoda, lighthouse ────────────────────────────────────────────

function tower(ctx: Ctx, b: Prop, day: number): void {
  const { w, d } = footprint(b)
  const x = b.x, y = b.y
  const H = wallHeight(b)
  const glass = b.color ?? '#8fa6ff'
  const night = 1 - day
  castShadow(ctx, x, y, w, d, Math.min(H, 4) + 1, 0.2)
  box(ctx, x, y, 0, w, d, H, shade(glass, -0.1), { faces: { top: true } })
  // Lobby band.
  poly(ctx, faceY(y + d + 0.002, x, x + w, 0, 1.4), mix('#2d3140', '#ffe3a0', night * 0.6))
  poly(ctx, faceX(x + w + 0.002, y, y + d, 0, 1.4), mix('#262a38', '#f0d390', night * 0.6))
  poly(ctx, faceY(y + d + 0.004, x + w / 2 - 0.45, x + w / 2 + 0.45, 0, 1.2), '#1d2030')
  // Window grid; some lit at night, different per building.
  const seed = Math.floor(x * 7 + y * 13)
  for (let z = 1.7; z < H - 0.3; z += 0.62) {
    for (let u = x + 0.25; u < x + w - 0.3; u += 0.62) {
      const on = night > 0.3 && hash01(Math.round(u * 10), Math.round(z * 10), seed) < 0.45
      poly(ctx, faceY(y + d + 0.003, u, u + 0.42, z, z + 0.4), on ? '#ffe39a' : mix(shade(glass, 0.25), '#1d2338', night * 0.8))
    }
    for (let u = y + 0.25; u < y + d - 0.3; u += 0.62) {
      const on = night > 0.3 && hash01(Math.round(u * 10), Math.round(z * 10), seed + 1) < 0.45
      poly(ctx, faceX(x + w + 0.003, u, u + 0.42, z, z + 0.4), on ? '#f0d48a' : mix(shade(glass, 0.05), '#161b2e', night * 0.8))
    }
  }
  // Roof: parapet, a water tank and an antenna with a blinking light.
  box(ctx, x, y, H, w, d, 0.2, shade(b.roof ?? '#4a5a9c', 0.1))
  box(ctx, x + w * 0.2, y + d * 0.2, H + 0.2, 0.9, 0.9, 0.6, '#9aa0b0')
  cyl(ctx, x + w * 0.7, y + d * 0.6, H + 0.2, 0.25, 0.7, '#b98559')
  const top = iso(x + w * 0.4, y + d * 0.7, H + 0.2)
  ctx.strokeStyle = '#3a3552'; ctx.lineWidth = 1.5
  ctx.beginPath(); ctx.moveTo(top.x, top.y); ctx.lineTo(top.x, top.y - 26); ctx.stroke()
  ctx.fillStyle = '#ff5d5d'; ctx.beginPath(); ctx.arc(top.x, top.y - 26, 2.4, 0, Math.PI * 2); ctx.fill()
  if (b.text) {
    // A big vertical neon sign down the +x face.
    const c = iso(x + w + 0.02, y + d * 0.5, H - 1.2)
    ctx.save(); ctx.translate(c.x, c.y); ctx.transform(1, -0.5, 0, 1, 0, 0)
    ctx.font = '900 13px Nunito, ui-rounded, system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    const col = ['#ff5dd8', '#4fd1ff', '#ffd166', '#7cf29c'][seed % 4]!
    ctx.shadowColor = col; ctx.shadowBlur = night * 10
    ctx.fillStyle = mix('#2d3140', col, 0.35 + 0.65 * night)
    ctx.fillRect(-14, -4 - b.text.length * 7, 28, b.text.length * 14 + 8)
    ctx.fillStyle = mix('#ffffff', '#ffffff', 1)
    for (const [i, ch] of [...b.text].entries()) ctx.fillText(ch, 0, -b.text.length * 7 + 4 + i * 14)
    ctx.restore()
  }
}

function pagoda(ctx: Ctx, b: Prop, _day: number): void {
  const { w, d } = footprint(b)
  const cx = b.x + w / 2, cy = b.y + d / 2
  const tiers = Math.max(2, Math.round(b.h ?? 3))
  const red = b.color ?? '#c0392b', roof = b.roof ?? '#2f3640'
  castShadow(ctx, b.x, b.y, w, d, 3, 0.18)
  box(ctx, b.x - 0.2, b.y - 0.2, 0, w + 0.4, d + 0.4, 0.4, '#d8d0c4')
  let z = 0.4
  for (let i = 0; i < tiers; i++) {
    const s = 1 - i * 0.2
    const hw = (w / 2) * s * 0.8, hd = (d / 2) * s * 0.8
    const th = 1.15 - i * 0.12
    box(ctx, cx - hw, cy - hd, z, hw * 2, hd * 2, th, red, { faces: { top: false } })
    // A lattice band of windows.
    poly(ctx, faceY(cy + hd + 0.003, cx - hw * 0.6, cx + hw * 0.6, z + th * 0.35, z + th * 0.8), '#f3d27a')
    poly(ctx, faceX(cx + hw + 0.003, cy - hd * 0.6, cy + hd * 0.6, z + th * 0.35, z + th * 0.8), '#d9b45a')
    z += th
    const o = 0.55 * s + 0.3
    const rx0 = cx - hw - o, rx1 = cx + hw + o, ry0 = cy - hd - o, ry1 = cy + hd + o
    const peak = z + 0.55
    poly(ctx, [iso(rx0, ry0, z), iso(rx1, ry0, z), iso(cx, cy, peak)], shade(roof, -0.25))
    poly(ctx, [iso(rx0, ry0, z), iso(rx0, ry1, z), iso(cx, cy, peak)], shade(roof, -0.2))
    poly(ctx, [iso(rx0, ry1, z), iso(rx1, ry1, z), iso(cx, cy, peak)], roof)
    poly(ctx, [iso(rx1, ry0, z), iso(rx1, ry1, z), iso(cx, cy, peak)], shade(roof, -0.12))
    upturn(ctx, [iso(rx0, ry1, z - 0.05), iso(rx1, ry1, z - 0.05), iso(rx1, ry0, z - 0.05)], roof)
    z += 0.35
  }
  const top = iso(cx, cy, z)
  ctx.strokeStyle = '#c9a24a'; ctx.lineWidth = 3
  ctx.beginPath(); ctx.moveTo(top.x, top.y); ctx.lineTo(top.x, top.y - 28); ctx.stroke()
  for (const k of [8, 14, 20]) { ctx.fillStyle = '#d9b45a'; ctx.beginPath(); ctx.ellipse(top.x, top.y - k, 4, 1.8, 0, 0, Math.PI * 2); ctx.fill() }
}

function lighthouse(ctx: Ctx, b: Prop, day: number): void {
  const { w, d } = footprint(b)
  const cx = b.x + w / 2, cy = b.y + d / 2
  const H = wallHeight(b)
  const r = Math.min(w, d) * 0.36
  castShadow(ctx, b.x, b.y, w, d, 4, 0.2)
  cyl(ctx, cx, cy, 0, r + 0.25, 0.5, '#b8b0a4')
  const bands = 5
  for (let i = 0; i < bands; i++) {
    const z0 = 0.5 + (i / bands) * (H - 0.5)
    cyl(ctx, cx, cy, z0, r * (1 - i * 0.05), (H - 0.5) / bands, i % 2 ? (b.roof ?? '#d94a5a') : (b.color ?? '#ffffff'))
  }
  cyl(ctx, cx, cy, H, r + 0.12, 0.12, '#3a3552')
  const night = 1 - day
  cyl(ctx, cx, cy, H + 0.12, r * 0.65, 0.75, '#3a3552', { top: '#3a3552' })
  const lamp = iso(cx, cy, H + 0.5)
  ctx.fillStyle = mix('#cfe8f5', '#fff3b0', night)
  ctx.beginPath(); ctx.ellipse(lamp.x, lamp.y, r * 0.5 * 32, 9, 0, 0, Math.PI * 2); ctx.fill()
  cyl(ctx, cx, cy, H + 0.87, r * 0.75, 0.12, b.roof ?? '#d94a5a')
  const tip = iso(cx, cy, H + 1.35)
  ctx.fillStyle = b.roof ?? '#d94a5a'
  const base = iso(cx, cy, H + 0.99)
  ctx.beginPath(); ctx.moveTo(base.x - r * 0.75 * 32, base.y); ctx.lineTo(tip.x, tip.y); ctx.lineTo(base.x + r * 0.75 * 32, base.y); ctx.closePath(); ctx.fill()
  poly(ctx, faceY(cy + r + 0.01, cx - 0.2, cx + 0.2, 0.5, 1.6), '#3a3552')
}

/** The lighthouse beam sweeping at night (drawn every frame, over everything else of it). */
export function lighthouseBeam(ctx: Ctx, b: Prop, t: number, day: number): void {
  const night = 1 - day
  if (night < 0.05) return
  const { w, d } = footprint(b)
  const lamp = iso(b.x + w / 2, b.y + d / 2, wallHeight(b) + 0.5)
  const a = t * 0.9
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.translate(lamp.x, lamp.y)
  ctx.scale(1, 0.5)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 320)
  g.addColorStop(0, `rgba(255,240,170,${0.45 * night})`)
  g.addColorStop(1, 'rgba(255,240,170,0)')
  ctx.fillStyle = g
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, 320, a - 0.16, a + 0.16); ctx.closePath(); ctx.fill()
  ctx.restore()
}

/** Where a cottage's chimney tops out (for smoke), or undefined if it has none. Mirrors roofFor. */
export function chimneyTop(b: Prop): { x: number; y: number; z: number } | undefined {
  if ((b.style ?? 'cottage') !== 'cottage') return undefined
  const { w, d } = footprint(b)
  if (w < d || hash01(b.x, b.y, 9) >= 0.7) return undefined
  const R = Math.min(w, d) * 0.42 + 0.3
  return { x: b.x + w * 0.25 + 0.2, y: b.y + d / 2 - 0.4, z: wallHeight(b) + R * 1.2 }
}
